// src/api/AuthSlice.ts
import pkg from "../../package.json";
import ApiSlice from "./slice";
import { api } from "./client";
import { getAppMarket } from "@/utils/market";
import { getRawVkLaunchParams } from "@/utils/vkMiniAppLaunchParams";

export type AuthLoginProvider =
    | "mts_id"
    | "greensms"
    | "telegram"
    | "whatsapp"
    | "call"
    | "vk"
    | "voice"
    | "sms"
    | "push"
    | "none"
    | "dev"
    | "unknown"
    | "debug_pass";

export type LoginResponse = {
    token: string;
    type: "register" | "login";
    provider: AuthLoginProvider;
    message?: string;
    hhe_uri?: string;
    auth_req_id?: string;
    correlation_id?: string;
};

export type VerifyLoginResponse = {
    token: string;
    type: string;
    mode?: "short";
    user?: { registration_completed?: boolean } & Record<string, unknown>;
};

export type MtsStatusResponse = {
    success?: boolean;
    provider: "mts_id";
    status?: string;
    authenticated?: boolean;
    sms_otp_ready?: boolean;
    can_verify_code?: boolean;
    action?: string;
    message?: string;
    error?: string;
    error_description?: string;
    token?: string;
    type?: "login" | "register";
    user?: { registration_completed?: boolean } & Record<string, unknown>;
    mts_status?: {
        success?: boolean;
        provider?: "mts_id";
        status?: string;
        authenticated?: boolean;
    };
};

export type TelegramAuthResponse = {
    success?: boolean;
    token?: string | null;
    access_token?: string | null;
    refresh_token?: string | null;
    type?: "register" | "login";
    registration_completed?: boolean;
    action?:
      | "login"
      | "contact_required"
      | "existing_account_found"
      | "registration"
      | "registration_required"
      | "account_not_found"
      | string;
    status?: string;
    link_token?: string | null;
    phone?: string | null;
    phone_confirmed?: boolean;
    need_bind_phone?: boolean;
    provider?: "google" | "apple" | "telegram" | "vk" | string;
    social_registration_token?: string | null;
    user?: ({
        id?: string | number;
        email?: string | null;
        name?: string | null;
        telegram_id?: string | number | null;
        phone?: string | null;
        phone_confirmed?: boolean;
        need_bind_phone?: boolean;
    } & Record<string, unknown>);
};

export type VkAuthPayload = {
    vk_launch_params: string;
    application: "vk-miniapp";
    language: string;
};

export async function getVkMiniAppAuthPayload(): Promise<VkAuthPayload | null> {
    const rawLaunchParams = getRawVkLaunchParams();
    if (!rawLaunchParams) return null;
    const launchParams = new URLSearchParams(rawLaunchParams);

    return {
        vk_launch_params: rawLaunchParams,
        application: "vk-miniapp",
        language: launchParams.get("vk_language") || "ru",
    };
}

function logVkAuthSummary(payload: VkAuthPayload, action?: string) {
    const launchParams = new URLSearchParams(payload.vk_launch_params);
    console.info("[VK auth]", {
        provider: "vk",
        vk_app_id: launchParams.get("vk_app_id"),
        vk_platform: launchParams.get("vk_platform"),
        has_sign: Boolean(launchParams.get("sign")),
        action: action || null,
    });
}

export type TelegramContact = {
    user_id: number | string;
    phone_number: string;
    first_name?: string;
    last_name?: string;
};

function getTelegramRequestContext() {
    const tgWebApp = (window as any).Telegram?.WebApp;
    const initData = tgWebApp?.initData || "";

    if (!initData) {
        throw new Error("Telegram WebApp initData недоступен");
    }

    return {
        initData,
        language: tgWebApp?.initDataUnsafe?.user?.language_code || navigator.language?.slice(0, 2) || "ru",
        application: "telegram-miniapp",
    };
}

export type EmailSendCodePayload = {
    email: string;
    brand?: "tinderone" | "scanlove" | null;
    platform?: string | null;
    app_version?: string | null;
};

export type EmailVerifyCodePayload = {
    email: string;
    code: string;
    language?: string | null;
};

export type EmailVerifyCodeResponse = {
    token?: string;
    type?: "login" | "register";
    need_bind_phone?: boolean;
};

export type InfoRegistrationPayload = {
    name: string;
    gender?: "male" | "female" | "m_f" | "m_m" | "f_f";
    sexual_orientation?:
    | "hetero"
    | "gay"
    | "lesbian"
    | "bisexual"
    | "asexual"
    | "not_decided";
    email?: string | null;
    birth_date: string;
    phone?: string;
    family_status?: "not_married" | "married" | "widow_er" | "divorced" | null;
    relationship_preference_id?: number | null;
    show_my_orientation?: boolean;
    show_my_gender?: boolean;
    show_me?: string[];
    interests?: number[];
    show_family_status?: boolean;
    birth_time?: string;
    referral_code?: string;
};

export type NotFinishedRegistrationUser = {
    id: number;
    phone?: string;
    email?: string;
    name?: string;
    step?: string;
};

export type InfoRegistrationResponse = {
    token?: string;
};

/** 🔥 Тип для корректного ответа Telegram-check */
export type CheckTelegramUserResponse = {
    exists: boolean;
    already_linked: boolean;
};

export default class AuthSlice extends ApiSlice {
    static domenURL = "/auth";

    static async TelegramAuth(type: "" | "register" | "login" = ""): Promise<TelegramAuthResponse> {
        const tgWebApp = (window as any).Telegram?.WebApp;
        const storedInitData = localStorage.getItem("telegram_init_data") || "";
        const isTelegramMiniApp = (window as any).launchEnv === "telegram";
        const initData = tgWebApp?.initData || (isTelegramMiniApp ? "" : storedInitData);

        if (!initData) {
            console.error("[TelegramAuth] Нет Telegram.WebApp.initData");
            throw new Error("Telegram WebApp initData недоступен");
        }

        const payload = {
            initData,
            language: tgWebApp?.initDataUnsafe?.user?.language_code || navigator.language?.slice(0, 2) || "ru",
            application: "telegram-miniapp",
        };

        const url = "/auth/telegram/v2";
        console.log("[TelegramAuth] POST", url, "type:", type, "body:", payload);

        const res = await api
            .post(url, payload, { timeout: 15000 })
            .then((response) => (response.data?.data || response.data) as TelegramAuthResponse);

        console.log("[TelegramAuth] response:", res);

        if (initData) {
            const current = localStorage.getItem("telegram_init_data");

            if (!current || current !== initData) {
                console.log("[TelegramAuth] обновляем telegram_init_data");
                localStorage.setItem("telegram_init_data", initData);
            }
        }

        return res;
    }

    static async ConfirmTelegramLink(linkToken: string): Promise<TelegramAuthResponse> {
        const tgWebApp = (window as any).Telegram?.WebApp;
        const initData = tgWebApp?.initData || localStorage.getItem("telegram_init_data") || "";

        if (!initData) {
            throw new Error("Telegram WebApp initData недоступен");
        }

        const response = await api.post(
            "/auth/telegram/v2/link/confirm",
            {
                initData,
                link_token: linkToken,
                language: tgWebApp?.initDataUnsafe?.user?.language_code || navigator.language?.slice(0, 2) || "ru",
                application: "telegram-miniapp",
            },
            { timeout: 15000 }
        );

        return (response.data?.data || response.data) as TelegramAuthResponse;
    }

    static async SubmitTelegramContact(contact: TelegramContact): Promise<TelegramAuthResponse> {
        const tgWebApp = (window as any).Telegram?.WebApp;
        const context = getTelegramRequestContext();

        const telegramUserId = tgWebApp?.initDataUnsafe?.user?.id;
        if (telegramUserId != null && String(contact.user_id) !== String(telegramUserId)) {
            throw new Error("TELEGRAM_CONTACT_OWNER_MISMATCH");
        }

        const response = await api.post(
            "/auth/telegram/v2/contact",
            {
                ...context,
                contact: {
                    user_id: contact.user_id,
                    phone_number: contact.phone_number,
                    first_name: contact.first_name || "",
                    last_name: contact.last_name || "",
                },
            },
            { timeout: 15000 }
        );

        return (response.data?.data || response.data) as TelegramAuthResponse;
    }

    static async GetTelegramContactStatus(): Promise<TelegramAuthResponse> {
        const response = await api.post(
            "/auth/telegram/v2/contact/status",
            getTelegramRequestContext(),
            { timeout: 3000 }
        );

        return (response.data?.data || response.data) as TelegramAuthResponse;
    }

    static async VkAuth(payload: VkAuthPayload | null): Promise<TelegramAuthResponse> {
        if (!payload?.vk_launch_params) {
            throw new Error("VK launch parameters are unavailable");
        }

        logVkAuthSummary(payload);
        const response = await api.post("/auth/vk", payload, { timeout: 15000 });
        const result = (response.data?.data || response.data) as TelegramAuthResponse;
        logVkAuthSummary(payload, result.action || result.type);
        return result;
    }

    static async Login(phone: string, device_token: string) {
        return this.request<LoginResponse>("/login", "POST", { phone, device_token });
    }

    static async VerifyLogin(code: string, token: string) {
        return this.request<VerifyLoginResponse>(
            "/verify-login",
            "POST",
            { code },
            { headers: { "Login-Token": token } }
        );
    }

    static async LoginFull(payload: { phone: string; device_token?: string; telegram?: string }) {
        return this.request<LoginResponse>("/login", "POST", payload);
    }

    static async GetMtsStatus(token: string) {
        return this.request<MtsStatusResponse>(
            "/mts/status",
            "GET",
            null,
            { headers: { "Login-Token": token } }
        );
    }

    static async VerifyLoginFull(
        payload: {
            code: string;
            language?: string;
            telegram?: string;
            social_registration_token?: string;
            provider?: string;
            vk_user_id?: string;
            access_token?: string;
            email?: string;
            application?: string;
            sign?: string;
            [key: `vk_${string}`]: string | undefined;
        },
        token: string
    ) {
        return this.request<VerifyLoginResponse>(
            "/verify-login",
            "POST",
            payload,
            { headers: { "Login-Token": token } }
        );
    }

    static async SendEmailLoginCode(payload: EmailSendCodePayload) {
        return this.request<Record<string, never>>("/email/send-code", "POST", payload);
    }

    static async VerifyEmailLoginCode(payload: EmailVerifyCodePayload) {
        return this.request<EmailVerifyCodeResponse>("/email/verify-code", "POST", payload);
    }

    static async PostInfoRegistration(
        payload: InfoRegistrationPayload,
        regToken?: string
    ): Promise<{ data: InfoRegistrationResponse }> {
        return this.request(
            "/users/infoRegistration",
            "POST",
            payload,
            { auth: true },
            undefined,
            ""
        );
    }

    static async SetMarket(market: string = getAppMarket()) {
        return this.request(
            "/users/market",
            "POST",
            { market },
            { auth: true },
            undefined,
            ""
        );
    }

    /** @deprecated */
    static async DeleteDeviceToken(token: string) {
        try {
            const res = await this.request(
                "/users/settings/token",
                "DELETE",
                { token },
                { auth: true },
                undefined,
                ""
            );
            console.log("[AuthSlice] Удалён device_token:", token);
            return res;
        } catch (e) {
            console.warn("[AuthSlice] Не удалось удалить device_token:", e);
        }
    }

    /** @deprecated */
    static async SendPushToken(customToken?: string) {
        const deviceToken =
            (window as any).OneSignal?.User?.onesignalId ??
            customToken ??
            "";

        const appVersion = `version: ${pkg.version}, market: ${getAppMarket()}`;
        const deviceInfo = [
            `os name: ${navigator.platform}`,
            `os version: ${navigator.userAgent}`,
            `brand: browser`,
            `hasGms: unknown`,
            `hasHms: unknown`,
        ].join(", ");

        return this.request(
            "/users/settings/token",
            "POST",
            {
                application: appVersion,
                device: deviceInfo,
                token: deviceToken,
            },
            { auth: true },
            undefined,
            ""
        );
    }

    static async UploadPhotos(files: File[]) {
        const formData = new FormData();
        files.forEach((f) => formData.append("photo[]", f));

        return this.request(
            "/users/photos",
            "POST",
            formData,
            {
                auth: true,
                headers: { "Content-Type": "multipart/form-data" },
            },
            undefined,
            ""
        );
    }

    static async setNotFinishedRegistrationUser(
        data: NotFinishedRegistrationUser
    ) {
        return this.request("/not-finished-registration-user", "POST", { ...data });
    }

    static async getNotFinishedRegistrationUserByPhone(phone: string) {
        return this.request<NotFinishedRegistrationUser>(
            `/not-finished-registration-user-by-phone/${phone}`
        );
    }

    static async getNotFinishedRegistrationUserByEmail(email: string) {
        return this.request<NotFinishedRegistrationUser>(
            `/not-finished-registration-user-by-email/${email}`
        );
    }

    /** 🔥 исправлено — добавлена строгая типизация */
    static async checkTelegramUser(phone: string) {
        return this.request<CheckTelegramUserResponse>(
            "/telegram/check",
            "POST",
            { phone }
        );
    }

    static async registerTelegramUser(data: { phone: string; telegram_id: string; name: string }) {
        return this.request("/telegram/register", "POST", data);
    }

    static async loginTelegramUser(data: { phone: string; telegram_id: string }) {
        return this.request("/telegram/login", "POST", data);
    }

}
